import React, { useContext } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useMutation } from '@apollo/client';
import { get } from 'lodash';

import { AccessPoint, SYNC_OPENING_HOURS } from '../query';
import { UpdateOpeningHours } from '../actions/UpdateOpeningHours';
import { useSpinner } from '../../../contexts/SpinnerContext';
import { useCustomToast } from '../../../hooks/useCustomToast';
import UserContext from '../../../contexts/UserContext';
import { getMomentFromTime, weekdayLabels } from '../openingHours';

interface OpeningHoursSectionProps {
  dropPointId: string;
  openingHours?: AccessPoint['openingHours'] | null;
  accessPointID?: string | null;
  na1adlActive?: boolean | null;
  refetch: () => Promise<any> | void;
}

const OpeningHoursSection = ({ dropPointId, openingHours, accessPointID, na1adlActive, refetch }: OpeningHoursSectionProps) => {
  const { toggleSpinner } = useSpinner();
  const { user } = useContext(UserContext);
  const toast = useCustomToast();
  const [synchronizeOpeningHours] = useMutation(SYNC_OPENING_HOURS);

  const handleSynchronizeOpeningHours = async () => {
    if (!accessPointID) {
      return;
    }

    try {
      toggleSpinner(true);
      await synchronizeOpeningHours({ variables: { accessPointID, userId: user.id } });
      toast({
        title: 'Erfolg!',
        description: 'Die Öffnungszeiten wurden synchronisiert.',
        status: 'success',
      });
      await refetch();
    } catch (error) {
      console.log(error);
      toast({
        description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
        status: 'error',
      });
    } finally {
      toggleSpinner(false);
    }
  };

  return (
    <Box mb="6">
      <Flex flexDirection="column" gap="2" mb="3">
        <Text fontWeight="bold" fontSize="lg">
          Öffnungszeiten:
        </Text>
        <Flex alignItems="center" gap="2" flexWrap="wrap">
          {accessPointID ? (
            <Button size="xs" colorScheme="brand" onClick={handleSynchronizeOpeningHours}>
              Öffnungszeiten Synchronisieren
            </Button>
          ) : null}
          <UpdateOpeningHours openingHours={openingHours} dropPointId={dropPointId} na1adlActive={na1adlActive} refetch={refetch} />
        </Flex>
      </Flex>
      {openingHours ? (
        Object.entries(openingHours).map(([day, hours]) => {
          if (day === '__typename' || day === 'type' || !Array.isArray(hours)) {
            return null;
          }

          return (
            <Flex key={day} direction="column" mb="2">
              <Text fontWeight="bold">{weekdayLabels[day]}</Text>
              {hours.length > 0 ? (
                hours.map(({ from, to }) => (
                  <Text key={`${day}-${from}-${to}`} fontSize="sm">
                    {getMomentFromTime(from).format('HH:mm')} Uhr - {getMomentFromTime(to).format('HH:mm')} Uhr
                  </Text>
                ))
              ) : (
                <Text fontSize="sm" color="gray.500">
                  Keine Zeiten hinterlegt
                </Text>
              )}
            </Flex>
          );
        })
      ) : (
        <Text fontSize="sm" color="gray.500">
          Öffnungszeiten wurden für diesen DropPoint noch nicht hinterlegt.
        </Text>
      )}
    </Box>
  );
};

export default OpeningHoursSection;
